import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type { AftercareTemplateRequest, AftercareTemplateResponse, ServiceResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useBranchPicker } from '../scheduling/useBranchPicker';

/**
 * What the clinic tells a patient after a treatment (DEC-025, FR-507).
 *
 * Freely editable, unlike a consent — aftercare is advice the clinic gives rather than an agreement
 * the patient made, so there is nothing a later edit could invalidate. What a particular patient
 * was told is snapshotted onto her visit at the moment of sending, which is why the screen carries
 * no versions and no publish step.
 *
 * One template per treatment, plus a clinic-wide default with no treatment on it — so a service
 * nobody has written instructions for still sends something. "Nothing was sent because nobody
 * configured it" is the failure automation is supposed to remove, not introduce.
 */
export function AftercarePage() {
  const { t } = useTranslation();
  const { clinicId } = useBranchPicker();
  const [editing, setEditing] = useState<AftercareTemplateResponse | 'new' | null>(null);

  const templates = useApiQuery<AftercareTemplateResponse[]>(
    queryKeys.aftercareTemplates(clinicId ?? ''),
    `/clinics/${clinicId}/clinical/aftercare`,
    { enabled: Boolean(clinicId) },
  );

  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId ?? ''),
    `/clinics/${clinicId}/catalog/services`,
    { enabled: Boolean(clinicId) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.aftercare')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.aftercare')}
        description={t('aftercare.description')}
        actions={
          <button type="button" className="button button--primary" onClick={() => setEditing('new')}>
            {t('aftercare.new')}
          </button>
        }
      />

      <AsyncSection
        query={templates}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('aftercare.emptyTitle')}
        emptyDescription={t('aftercare.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('booking.service')}</th>
                  <th scope="col">{t('aftercare.title')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((template) => (
                  <tr key={template.id}>
                    <th scope="row">
                      {template.serviceId
                        ? (template.serviceNameEn ?? template.serviceNameAr)
                        : t('aftercare.clinicDefault')}
                    </th>
                    <td>{template.titleAr ?? template.titleEn}</td>
                    <td>{template.active ? t('common.active') : t('common.inactive')}</td>
                    <td>
                      <button
                        type="button"
                        className="button button--compact"
                        onClick={() => setEditing(template)}
                      >
                        {t('common.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      <p className="field__hint">{t('aftercare.snapshotHint')}</p>

      {editing ? (
        <AftercareDialog
          clinicId={clinicId}
          template={editing === 'new' ? null : editing}
          services={services.data ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function AftercareDialog({
  clinicId,
  template,
  services,
  onClose,
}: {
  readonly clinicId: string;
  readonly template: AftercareTemplateResponse | null;
  readonly services: readonly ServiceResponse[];
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const [serviceId, setServiceId] = useState(template?.serviceId ?? '');
  const [titleAr, setTitleAr] = useState(template?.titleAr ?? '');
  const [titleEn, setTitleEn] = useState(template?.titleEn ?? '');
  const [bodyAr, setBodyAr] = useState(template?.bodyAr ?? '');
  const [bodyEn, setBodyEn] = useState(template?.bodyEn ?? '');

  const save = useApiMutation<unknown, AftercareTemplateRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/clinical/aftercare`,
      options: { method: 'PUT', body },
    }),
    { invalidates: [queryKeys.aftercareTemplates(clinicId)], onSuccess: onClose },
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('nav.items.aftercare')}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{template ? t('common.edit') : t('aftercare.new')}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>
        <div className="modal__body">
          <label className="field">
            <span className="field__label">{t('booking.service')}</span>
            <select
              className="field__input"
              value={serviceId}
              onChange={(event) => setServiceId(event.target.value)}
              disabled={Boolean(template)}
            >
              <option value="">{t('aftercare.clinicDefault')}</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.nameEn ?? service.nameAr}
                </option>
              ))}
            </select>
          </label>

          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('consents.titleAr')}</span>
              <input
                className="field__input"
                value={titleAr}
                onChange={(event) => setTitleAr(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">{t('consents.titleEn')}</span>
              <input
                className="field__input ltr"
                value={titleEn}
                onChange={(event) => setTitleEn(event.target.value)}
                required
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">{t('consents.bodyAr')}</span>
            <textarea
              className="field__input"
              rows={8}
              value={bodyAr}
              onChange={(event) => setBodyAr(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">{t('consents.bodyEn')}</span>
            <textarea
              className="field__input ltr"
              rows={8}
              value={bodyEn}
              onChange={(event) => setBodyEn(event.target.value)}
              required
            />
          </label>

          {save.error ? <ErrorState error={save.error} /> : null}
          <p className="field__hint">{t('aftercare.bothLanguagesHint')}</p>
        </div>
        <footer className="modal__footer">
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                serviceId: serviceId || undefined,
                titleAr,
                titleEn,
                bodyAr,
                bodyEn,
              })
            }
          >
            {t('common.save')}
          </button>
        </footer>
      </div>
    </div>
  );
}
